import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlarmClock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;

export const OverdueReminderSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(9);
  const [stages, setStages] = useState("7, 30");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [{ data }, { data: p }] = await Promise.all([axios.get(`${API}/notifications/overdue-settings`), axios.get(`${API}/notifications/overdue-preview`)]);
    setCfg(data); setEnabled(data.enabled); setHour(data.hour); setStages(data.stages.join(", ")); setPreview(p);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const parsedStages = stages.split(/[,\s]+/).map((s) => parseInt(s, 10)).filter((n) => n > 0);
  const save = async () => {
    setBusy("save");
    try {
      const { data } = await axios.put(`${API}/notifications/overdue-settings`, { enabled, hour, stages: parsedStages });
      setCfg(data); setStages(data.stages.join(", "));
      toast.success(enabled ? `Overdue reminders on — checked daily at ${hourLabel(hour)} ET (${data.stages.join(" & ")} days past due)` : "Overdue reminders turned off");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(null);
    }
  };
  const sendNow = async () => {
    if (!window.confirm(`Send overdue reminder emails now to ${preview?.count || 0} customer invoice(s)?`)) return;
    setBusy("send");
    try {
      const { data } = await axios.post(`${API}/notifications/send-overdue-reminders`);
      if (data.reminders_sent === 0 && data.errors.length === 0) toast.info("No invoices need a reminder right now");
      else if (data.errors.length) toast.warning(`Sent ${data.reminders_sent}; ${data.errors.length} error(s): ${data.errors[0]}`);
      else toast.success(`Sent ${data.reminders_sent} overdue reminder email(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminders");
    } finally {
      setBusy(null);
    }
  };

  const dirty = cfg && (cfg.enabled !== enabled || cfg.hour !== hour || cfg.stages.join(",") !== parsedStages.join(","));
  const last = cfg?.last_result;
  return (
    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4" data-testid="overdue-reminder-settings">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlarmClock className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Automatic Overdue Invoice Reminders</p>
            <p className="text-sm text-amber-800">Customers with an unpaid balance get a friendly branded reminder (Cash App / Zelle QR codes, View &amp; Pay link, PDF) when an invoice is 7 and 30 days past due. Each stage is sent once per invoice.</p>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="overdue-enabled-toggle" />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Label className="text-amber-900">Check time (Eastern)</Label>
          <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
            <SelectTrigger className="bg-white" data-testid="overdue-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-amber-900">Days past due</Label>
          <Input value={stages} onChange={(e) => setStages(e.target.value)} className="bg-white" placeholder="7, 30" data-testid="overdue-stages-input" />
        </div>
        <Button onClick={save} disabled={!dirty || !!busy || parsedStages.length === 0} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="overdue-save-button">{busy === "save" ? "Saving…" : "Save"}</Button>
        <Button onClick={sendNow} disabled={!!busy || !preview?.count} size="sm" variant="outline" className="bg-white" data-testid="overdue-send-now-button">
          <AlarmClock className="w-4 h-4 mr-2" />{busy === "send" ? "Sending…" : `Send Reminders Now${preview ? ` (${preview.count})` : ""}`}
        </Button>
      </div>
      {preview && preview.count > 0 && (
        <p className="text-xs text-amber-900" data-testid="overdue-preview">
          Waiting for a reminder: {preview.invoices.slice(0, 4).map((i) => `${i.invoice_number} (${i.days_overdue}d, $${i.balance_due.toFixed(2)})`).join(", ")}{preview.count > 4 ? ` +${preview.count - 4} more` : ""}
        </p>
      )}
      <p className="text-xs text-amber-800" data-testid="overdue-last-run">
        Last run: {cfg?.last_run ? new Date(cfg.last_run).toLocaleString() : "never"}{last ? ` · ${last.reminders_sent} sent, ${last.errors?.length || 0} error(s) (${last.trigger})` : ""}
      </p>
    </div>
  );
};
